import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";
import useSeo from "@/hooks/useSeo";

export default function PrivacyPolicy() {
  useSeo({
    title: "Privacy Policy — WebJesters",
    description:
      "How WebJesters collects, uses, and protects your data — quote form details, payment info, hosted client sites, and the WebJesters client portal.",
    path: "/privacy-policy",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Privacy Policy", item: "https://webjesters.com/privacy-policy" },
      ],
    },
  });
  return (
    <LegalLayout
      title="Privacy Policy"
      eyebrow="Legal"
      lastUpdated="October 16, 2026"
    >
      <p>
        This Privacy Policy explains what WebJesters LLC, an Arizona limited
        liability company ("WebJesters," "we," "us," or "our"), collects,
        why, and how we look after it. It covers our marketing site at{" "}
        <a
          href="https://webjesters.com"
          className="text-white underline underline-offset-4 hover:text-zinc-100"
        >
          webjesters.com
        </a>
        , our hosted client sites, and the WebJesters client portal. Our{" "}
        <a href="/cookie-policy" className="text-white underline underline-offset-4 hover:text-zinc-100">Cookie Policy</a>{" "}
        is part of this policy.
      </p>
      <p>
        <strong className="text-white">The short version:</strong> we collect
        what you give us to quote and run your project, we don't sell it,
        advertising cookies stay off unless you accept them, and you can ask
        us to show, fix, or delete your data any time.
      </p>

      <LegalSection title="What we collect">
        <p>
          <strong className="text-white">Quote &amp; contact form:</strong>{" "}
          your name, email, phone number, business name, and any project
          details you choose to share.
        </p>
        <p>
          <strong className="text-white">Reservations &amp; payments:</strong>{" "}
          your email and business name when you reserve a build slot. We
          never receive or store your card details — Stripe handles checkout
          on its own secure pages and shares only what we need to fulfill
          the order (transaction status, last 4 digits for receipts).
        </p>
        <p>
          <strong className="text-white">Client portal &amp; account:</strong>{" "}
          once you're a website client, your portal stores your business
          profile, domain and DNS settings, leads forwarded from your site,
          project notes, and any updates we exchange. Access is limited to you
          and authorized WebJesters team members. Local Visibility-only
          customers do not receive portal access.
        </p>
        <p>
          <strong className="text-white">Uploaded media:</strong> photos,
          logos, copy, or other assets you send us for your site. You're
          responsible for having the right to use anything you upload.
        </p>
        <p>
          <strong className="text-white">
            Local Visibility content &amp; account access:
          </strong>{" "}
          if you use Local Visibility, we receive the photos, clips, captions,
          and business updates you send us by text, email, or a download link
          you share (for example Google Drive, Dropbox, or iCloud). We store
          that material only to create and publish your posts, and we keep
          drafts and approvals so we can show what was published. We also work
          inside your Google Business Profile and chosen social platform
          through manager access you grant us. You remain the owner of those
          accounts, we only use that access to publish approved content and
          keep your profile details current, and you can remove our access at
          any time.
        </p>
        <p>
          <strong className="text-white">Automatically collected:</strong>{" "}
          like most websites, our servers log your IP address, browser type,
          pages requested, and timestamps for security and rate limiting.
          Form submissions record the time and a simple spam check. These
          logs are kept briefly and are not used to build profiles.
        </p>
        <p>
          <strong className="text-white">Hosted client sites:</strong> when
          someone fills out a form on a website we host for a client, that
          information belongs to the client and is forwarded to them. We
          process it on the client's behalf under our agreement with them, and
          requests about it should go to that business.
        </p>
      </LegalSection>

      <LegalSection title="How we use it">
        <p>We use your information only to:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Provide quotes and run your project</li>
          <li>
            Send transactional emails and SMS — quote confirmations, project
            updates, payment receipts, and account messages
          </li>
          <li>Operate, secure, and improve your hosted site</li>
          <li>
            Create, get your approval for, and publish Local Visibility
            content if you use that service
          </li>
          <li>Respond to support requests</li>
          <li>
            Measure whether a Google ad led to an inquiry or purchase — only
            if you accept advertising cookies
          </li>
          <li>Prevent fraud and abuse, keep records, and meet legal obligations</li>
        </ul>
        <p>
          We do not sell your information. We do not share it with third
          parties for their own marketing. We do not use automated
          decision-making that produces legal or similarly significant effects
          about you.
        </p>
      </LegalSection>

      <LegalSection title="SMS messaging">
        <p>
          By submitting our contact form and consenting to texts, you agree
          to receive transactional SMS messages from WebJesters about your
          quote and account. Consent is not a condition of purchase. Message
          and data rates may apply. Message frequency varies. Reply STOP to
          opt out, HELP for support.
        </p>
        <p
          data-testid="twilio-mobile-clause"
          className="text-white font-medium"
        >
          Mobile information will not be shared with third parties or
          affiliates for marketing/promotional purposes.
        </p>
      </LegalSection>

      <LegalSection title="Email communications">
        <p>
          Transactional emails (quote confirmations, project updates,
          payment receipts) are sent through Resend. You can reply to any of
          them to reach our team at{" "}
          <a
            href="mailto:team@webjesters.com"
            className="text-white underline underline-offset-4 hover:text-zinc-100"
          >
            team@webjesters.com
          </a>
          . Each email includes an unsubscribe link.
        </p>
      </LegalSection>

      <LegalSection title="Cookies, advertising measurement &amp; your choices" id="cookies">
        <p>
          webjesters.com runs no analytics suite, social pixels, or
          session-recording tools. The only optional tracking is{" "}
          <strong className="text-white">Google Ads conversion measurement</strong>,
          which tells us whether a Google ad brought in an inquiry. That script
          is not loaded until you click <strong className="text-white">Accept</strong>{" "}
          in our cookie banner; if you decline or never answer, it stays off.
          We use Google's Consent Mode so your choice is passed to Google.
        </p>
        <p data-testid="privacy-gpc">
          We honor the <strong className="text-white">Global Privacy Control</strong>{" "}
          browser signal as an opt-out. You can change your choice any time via
          "Do Not Sell or Share My Personal Information" in the footer. Full
          details, including a cookie table, are in our{" "}
          <a href="/cookie-policy" className="text-white underline underline-offset-4 hover:text-zinc-100">Cookie Policy</a>.
          Hosted client sites only carry analytics or pixels when the client
          asks us to add them.
        </p>
      </LegalSection>

      <LegalSection title="How long we keep it" id="retention">
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>
            <strong className="text-white">Inquiries and review requests</strong>{" "}
            — up to 24 months after our last contact, then deleted.
          </li>
          <li>
            <strong className="text-white">Client account and project data</strong>{" "}
            — for the life of the service, then up to 90 days in backups
            before permanent deletion (content exports are available on
            request).
          </li>
          <li>
            <strong className="text-white">Payment and tax records</strong>{" "}
            — 7 years, as required for accounting and tax purposes.
          </li>
          <li>
            <strong className="text-white">SMS consent and opt-out records</strong>{" "}
            — 4 years, to document compliance.
          </li>
          <li>
            <strong className="text-white">Server and security logs</strong>{" "}
            — typically 30 to 90 days.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Storage &amp; security">
        <p>
          Information you submit is stored in secure systems and databases
          and is accessible only to authorized WebJesters team members. All
          data in transit uses HTTPS. We retain information for as long as
          needed to provide the service and meet legal obligations.
        </p>
        <p>
          Following account cancellation, information and secure backups
          may be retained for a limited period for disaster recovery, fraud
          prevention, legal compliance, and operational continuity before
          permanent deletion.
        </p>
        <p>
          While we take reasonable steps to protect your information, no
          online service can guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="Service providers we work with">
        <p>To run WebJesters, we rely on a small set of trusted providers:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>
            <strong className="text-white">Stripe</strong> — payment
            processing for deposits and monthly subscriptions
          </li>
          <li>
            <strong className="text-white">Resend</strong> — transactional
            email delivery
          </li>
          <li>
            <strong className="text-white">Twilio</strong> — SMS messaging
          </li>
          <li>
            <strong className="text-white">MongoDB &amp; cloud hosting</strong>{" "}
            — secure storage and infrastructure for our platform and your
            hosted site
          </li>
          <li>
            <strong className="text-white">Google Ads</strong> — conversion
            measurement, only after you accept advertising cookies
          </li>
          <li>
            <strong className="text-white">Cloudflare</strong> — content
            delivery, DDoS protection, and edge hosting for our sites
          </li>
          <li>
            <strong className="text-white">
              Google Business Profile &amp; your chosen social platform
            </strong>{" "}
            — where Local Visibility content is published, under manager
            access you grant and control
          </li>
        </ul>
        <p>
          Each provider uses your information solely to deliver the service
          we've engaged them for, under their own privacy terms. We may also
          disclose information when the law requires it, to protect our rights
          or safety, or to a successor if our business is sold or merged (we'd
          notify you first).
        </p>
        <p>
          We are based in the United States and process data there. If you use
          our services from elsewhere, you understand your information is
          transferred to and handled in the U.S.
        </p>
      </LegalSection>

      <LegalSection title="Your rights" id="rights">
        <p>
          You can request a copy of the information we hold about you, ask
          us to correct it, or ask us to delete it at any time. Email{" "}
          <a
            href="mailto:team@webjesters.com?subject=Privacy%20request"
            className="text-white underline underline-offset-4 hover:text-zinc-100"
          >
            team@webjesters.com
          </a>{" "}
          and we'll respond within 30 days (45 where state law allows, and
          we'll tell you if we need the extra time). We'll verify the request
          by matching it to the email or phone number on file, and we'll never
          charge for a reasonable request or treat you differently for making
          one.
        </p>
      </LegalSection>

      <LegalSection title="State privacy rights (California and other states)" id="state-rights">
        <p>
          If you live in California, Colorado, Connecticut, Delaware, Indiana,
          Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New
          Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah,
          Virginia, or another state with a comprehensive privacy law, you may
          have the right to:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 text-zinc-300">
          <li>Know what personal information we collect, use, and disclose</li>
          <li>Access it in a portable format, correct it, or delete it</li>
          <li>
            Opt out of "sales," "sharing," or targeted advertising — we don't
            sell data; our only ad-related activity is the optional Google
            Ads cookie, which you control via the cookie banner, the "Do Not
            Sell or Share" link, or a Global Privacy Control signal
          </li>
          <li>Opt out of profiling in furtherance of significant decisions (we don't do this)</li>
          <li>Not be discriminated against for exercising these rights</li>
          <li>
            Appeal a decision we make about your request — reply to our
            response and a different team member will review it within 45 days
          </li>
        </ul>
        <p>
          <strong className="text-white">California notice at collection.</strong>{" "}
          In the past 12 months we collected identifiers (name, email, phone,
          business name, IP address), commercial information (services
          purchased), internet activity (pages requested; ad-conversion data
          only with consent), and the content of your communications. Sources:
          you, your devices, and Stripe. Purposes and retention are described
          above. We do not collect sensitive personal information as defined
          by California law and do not knowingly sell or share the personal
          information of anyone under 16. An authorized agent may submit a
          request on your behalf with written permission from you.
        </p>
      </LegalSection>

      <LegalSection title="Children's privacy">
        <p>
          WebJesters' services are intended for business owners and adults.
          We do not knowingly collect personal information from anyone under
          18, and never from children under 13. If we become aware that we
          have, we will promptly delete it.
        </p>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. The revised
          version will be posted here with an updated "Last updated" date.
          Material changes will also be communicated by email when
          appropriate.
        </p>
      </LegalSection>

      <LegalSection title="Contact us">
        <p>
          Questions about this policy or how we handle your data? Reach us
          at{" "}
          <a
            href="mailto:team@webjesters.com"
            className="text-white underline underline-offset-4 hover:text-zinc-100"
          >
            team@webjesters.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
